import React, { useState, useEffect } from 'react';
import { Clock, ArrowUpCircle, ArrowDownCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { billingAPI } from '../../services/api';

const typeConfig = {
  top_up: { icon: ArrowUpCircle, color: 'text-green-500', label: 'Top-up' },
  auto_recharge: { icon: RefreshCw, color: 'text-blue-500', label: 'Auto-recharge' },
  usage: { icon: ArrowDownCircle, color: 'text-red-500', label: 'Usage' },
  refund: { icon: ArrowUpCircle, color: 'text-purple-500', label: 'Refund' },
  adjustment: { icon: RefreshCw, color: 'text-gray-500', label: 'Adjustment' },
};

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [filter, setFilter] = useState('all');

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      };
      if (filter !== 'all') {
        params.type = filter;
      }
      const response = await billingAPI.getTransactions(params);
      if (response.data.success) {
        setTransactions(response.data.transactions);
        setPagination((prev) => ({
          ...prev,
          total: response.data.total,
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [pagination.offset, filter]);

  const formatCents = (cents) => {
    const isPositive = cents >= 0;
    return `${isPositive ? '+' : ''}${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100)}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  const goToPage = (page) => {
    setPagination((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-freshroad-black flex items-center gap-2">
          <Clock className="w-5 h-5 text-freshroad-teal" />
          Transaction History
        </h3>
        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPagination((prev) => ({ ...prev, offset: 0 }));
          }}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-freshroad-teal focus:border-transparent"
        >
          <option value="all">All Transactions</option>
          <option value="top_up">Top-ups</option>
          <option value="auto_recharge">Auto-recharges</option>
          <option value="usage">Usage</option>
          <option value="refund">Refunds</option>
        </select>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading transactions...</div>
      ) : error ? (
        <div className="p-8 text-center text-red-500">{error}</div>
      ) : transactions.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No transactions found</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map((tx) => {
                  const config = typeConfig[tx.type] || typeConfig.adjustment;
                  const Icon = config.icon;
                  return (
                    <tr key={tx._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${config.color}`} />
                          <span className="text-sm font-medium text-gray-900">
                            {config.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {tx.description || tx.creditAction || '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium text-right ${
                        tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCents(tx.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">
                        {formatCents(tx.balanceAfter)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {formatDate(tx.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionHistory;
